import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { formatCOP, useSeo } from "@/lib/site";

export default function PaymentResultPage() {
  useSeo({ title: "Estado de tu pago | LUMINA STUDIO", noindex: true });
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const id = params.get("id"), ref = params.get("ref");
    let stop = false, timer;
    const check = async () => {
      try {
        const { data } = id ? await api.post("/checkout/verify", { transaction_id: id }) : await api.get(`/checkout/status/${ref}`);
        if (stop) return;
        setRes(data);
        if (data.status === "APPROVED") { localStorage.removeItem("lumina_pending_payment"); qc.invalidateQueries({ queryKey: ["cart"] }); }
        else if (data.status === "PENDING") timer = setTimeout(check, 4000);
      } catch (e) { if (!stop) setError(errMsg(e)); }
    };
    if (id || ref) check(); else setError("No recibimos la información del pago.");
    return () => { stop = true; clearTimeout(timer); };
  }, [params, qc]);

  const box = "mx-auto max-w-xl px-5 py-20 text-center sm:py-28";
  if (error) return <div data-testid="payment-result-error" className={box}><XCircle className="mx-auto h-10 w-10 text-[#8B1E1E]" /><p className="mt-4">{error}</p><Link to="/carrito" className="mt-6 inline-block underline">Volver al carrito</Link></div>;
  if (!res || res.status === "PENDING") return (
    <div data-testid="payment-result-pending" className={box}><Loader2 className="mx-auto h-10 w-10 animate-spin text-[#B18A4B]" />
      <h1 className="mt-6 font-serif-display text-3xl">Verificando tu pago…</h1><p className="mt-3 text-[#080808]/70">Estamos esperando la confirmación de Wompi. No cierres esta página.</p></div>
  );
  if (res.status !== "APPROVED") return (
    <div data-testid="payment-result-declined" className={box}><XCircle className="mx-auto h-10 w-10 text-[#8B1E1E]" />
      <h1 className="mt-6 font-serif-display text-3xl">El pago no fue aprobado</h1><p className="mt-3 text-[#080808]/70">No se realizó ningún cobro. Tu carrito sigue guardado para que lo intentes de nuevo.</p>
      <Link to="/checkout" className="mt-8 inline-flex rounded-full bg-[#080808] px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4]">Intentar de nuevo</Link></div>
  );
  return (
    <div data-testid="payment-result-approved" className={box}><CheckCircle2 className="mx-auto h-12 w-12 text-[#B18A4B]" />
      <h1 className="mt-6 font-serif-display text-[34px] font-medium">¡Pago aprobado!</h1>
      <p className="mt-3 text-lg">Pedido <b data-testid="order-number">{res.order_number}</b> · {formatCOP(res.total_cop)}</p>
      <p className="mt-4 text-[#080808]/75">Tu LUMINA está en producción. Pronto recibirás tu recuerdo flotante. Te enviamos el resumen a tu correo.</p>
      <Link to="/" className="mt-8 inline-block text-sm font-semibold uppercase tracking-[0.14em] underline underline-offset-4">Volver al inicio</Link></div>
  );
}
